import { useState } from 'react';

import { useUnmount } from 'ahooks';

import { UserPermission } from '@cobliteam/common-types/src/user';

import type { ChatHistory } from '@cobliteam/api-lib';

import { Analytics } from '@cobliteam/analytics';

import { useGetChatHistory, useRequestResponse } from '@cobliteam/hooks';

import { DefaultInput } from '@cobliteam/base-components/src/Input/DefaultInput';
import withPermission from '@cobliteam/base-components/src/PermissionsGate/PermissionsGate';

import { Alert, Button, Dialog, Typography } from '@cobliteam/ui/components';

import ArrowCircleUpOutlinedIcon from '@mui/icons-material/ArrowCircleUpOutlined';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';

import ChatDisplay from './components/ChatDisplay';
import ChatHistoryModule from './components/ChatHistoryModule';
import LandingContent from './components/LandingContent';

import useNavbarStore from '../../../../zustandStores/useNavbarStore';

interface CoblinatorProps {
  open: boolean;
  onOpen: (open: boolean) => void;
}

const CoblinatorModal = ({ open, onOpen }: CoblinatorProps) => {
  const [coblinatorPrompt, setCoblinatorPrompt] = useState<string>('');
  const [aiResponseFromHistory, setAIResponseFromHistory] = useState<ChatHistory | undefined>(undefined);

  const [submitedUserPrompt, setSubmitedUserPrompt] = useNavbarStore((state) => [state.submitedUserPrompt, state.setSubmitedUserPrompt]);
  const [currentPromptAIResponse, setCurrentPromptAIResponse] = useNavbarStore((state) => [state.currentPromptAIResponse, state.setCurrentPromptAIResponse]);
  const [isLoadingAIResponse, setIsLoadingAIResponse] = useNavbarStore((state) => [state.isLoadingAIResponse, state.setIsLoadingAIResponse]);

  const { mutateAsync: requestResponse } = useRequestResponse();
  const { refetch: refetchChatHistory } = useGetChatHistory();

  useUnmount(() => {
    if (!isLoadingAIResponse) {
      setSubmitedUserPrompt(undefined);
      setCurrentPromptAIResponse(undefined);
    }
  });

  const handleRequestResponse = async (prompt?: string) => {
    setSubmitedUserPrompt(prompt ? prompt : coblinatorPrompt);
    setCurrentPromptAIResponse(undefined);
    setAIResponseFromHistory(undefined);
    setIsLoadingAIResponse(true);
    setCoblinatorPrompt('');

    try {
      const request = {
        prompt: prompt ? prompt : coblinatorPrompt,
      };
      Analytics.track('Coblinator Request Response', request);

      const response = await requestResponse({ request });
      Analytics.track('Coblinator Response', { content: response.content });
      setCurrentPromptAIResponse(response);
      refetchChatHistory();
    } catch (error) {
      Analytics.track('Coblinator Request Response Error', { error: error });
      Alert.error('Erro durante a consulta. Tente novamente mais tarde.');
    } finally {
      setIsLoadingAIResponse(false);
    }
  };

  const handleEnter = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') await handleRequestResponse();
  };

  return (
    <Dialog open={open} onOpenChange={onOpen}>
      <Dialog.Content modalSize='lg' className='flex flex-row p-0 gap-0 h-[650px]'>
        <ChatHistoryModule onSetSubmitedUserPrompt={setSubmitedUserPrompt} onSetAIResponse={setAIResponseFromHistory} />
        <div className='w-full'>
          <Dialog.Header className='pt-5'>
            <Typography variant='heading-2' color='secondary'>
              Coblinator
            </Typography>
            <Dialog.Close />
          </Dialog.Header>
          <Dialog.Body className='flex flex-col px-10 pt-5 h-[88%] gap-10 overflow-y-auto'>
            <div className='flex flex-col items-center gap-3'>
              <DefaultInput
                endAdornment={<ArrowCircleUpOutlinedIcon onClick={() => handleRequestResponse()} className='cursor-pointer' />}
                placeholder={submitedUserPrompt ? 'Iniciar novo chat' : 'Pergunte ao Coblinator'}
                value={coblinatorPrompt}
                className='w-full min-w-72 mt-3'
                onKeyDown={handleEnter}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setCoblinatorPrompt(event.target.value)}
                disabled={isLoadingAIResponse}
              />
              <Typography variant='caption-default' color='secondary'>
                As perguntas são respondidas por AI e podem conter erros. Verifique as informações importantes.
              </Typography>
            </div>
            <>
              {!submitedUserPrompt && <LandingContent onPromptClick={handleRequestResponse} />}
              {submitedUserPrompt && <ChatDisplay userPrompt={submitedUserPrompt} aiResponse={aiResponseFromHistory || currentPromptAIResponse} isLoading={isLoadingAIResponse} />}
            </>
          </Dialog.Body>
        </div>
      </Dialog.Content>
    </Dialog>
  );
};

const CoblinatorModule = () => {
  const [openDialog, setOpenDialog] = useState<boolean>(false);

  const handleClick = () => {
    Analytics.track('Coblinator Open Modal');
    setOpenDialog(true);
  };

  return (
    <div className='justify-center align-middle'>
      <Button onClick={handleClick} variant='outlined' color='secondary' size='condensed' startIcon={<AutoAwesomeRoundedIcon color='primary' />}>
        Coblinator
      </Button>

      {openDialog ? <CoblinatorModal open={openDialog} onOpen={setOpenDialog} /> : null}
    </div>
  );
};

export default withPermission(UserPermission.VIEW_ANALYTICS_AND_REPORTS, CoblinatorModule);
